import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux';
import { setModal, selectModal } from "../../../redux/modalSlice";
import { useForm } from 'react-hook-form'
import { useRequest } from '../../../hooks/UseRequest'
import { selectModules } from '../../../redux/moduleSlice'
import { setNotification } from '../../../redux/notificationSlice'
import Loading from '../../../components/Loading'
import { selectDropdowns, selectDropdownOptions } from '../../../redux/dropdownSlice'
import FormFieldBuilder from "../../../components/FormFieldBuilder";
import { formDataSanitizer } from '../../../helpers/formValidation'

export default function LeadDetailsModal() {
  const dispatch = useDispatch();
  const modal = useSelector(selectModal);
  const { register, handleSubmit, control, formState: { errors }, reset } = useForm();
  const [saving, setSaving] = useState(false)
  const request = useRequest()
  const latestLeadId = modal.options?.latestLeadId
  const customerId = modal.options?.customerId
  const active = modal.is_active && modal.type === 'lead_details_modal'
  const inFlightLeadId = useRef(null)
  const [leadInfo, setLeadInfo] = useState(null)
  const [loadedLeadId, setLoadedLeadId] = useState(null)
  const dropdownOptions = useSelector(selectDropdownOptions)

  const [module, setModule] = useState()
  const [moduleFields, setModuleFields] = useState()
  const navi = useNavigate()
  const modules = useSelector(selectModules)
  const dropDowns = useSelector(selectDropdowns)
  const [stateFields, setStateFields] = useState([]);

  const leadFields = ['tags']

  const handleStateFields = (name, value, module) => {
    const stateFieldName = `${module.code_key}_${name}`;
    if (stateFields.some((item) => item.name === stateFieldName)) {
      setStateFields(stateFields.map((item) => {
        if (stateFieldName === item?.name) {
          return {
            ...item,
            value: value
          }
        } else {
          return item
        }
      }))
    } else {
      setStateFields([
        ...stateFields,
        {
          name: stateFieldName,
          value: value
        }
      ])
    }
  }

  useEffect(() => {
    if (modules.data.length > 0) {
      const m = modules.data.find((e) => e.code_key === 'lead')

      if (m) {
        setModule(m)

        const mf = [...m.module_fields]
        mf.sort((a, b) => a.detail_display_rank - b.detail_display_rank)
        setModuleFields(mf)
      }
    }
  }, [modules.data])

  useEffect(() => {
    return () => {
      setLeadInfo(null);
    };
  }, []);

  // This modal stays mounted in the Footer and its fields take the loaded lead only as defaultValue
  // (applied once at registration). Clear the previous lead on every open and drop late responses,
  // so a previous lead's values can never become this lead's form values.
  useEffect(() => {
    if (active && latestLeadId) {
      resetState()
      getleadInformation(latestLeadId)
    } else {
      inFlightLeadId.current = null
    }
  }, [active, latestLeadId])
  const getleadInformation = async (latestLeadId) => {
    inFlightLeadId.current = latestLeadId
    request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/lead/${latestLeadId}`)
    try {
      const response = await request.get()
      if (inFlightLeadId.current !== latestLeadId) return
      if (response.status === 200 || response.status === 201) {
        setLeadInfo(response?.data?.data)
        setLoadedLeadId(latestLeadId)
      } else {
        dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
      }
    } catch (error) {
      if (inFlightLeadId.current !== latestLeadId) return
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
    }
  }

  const resetState = () => {
    setLeadInfo(null);
    setLoadedLeadId(null);
    setStateFields([]);
    setSaving(false);
    reset();
  };

  const handleCancel = () => {
    resetState();
    dispatch(setModal({ is_active: false }));
  };

  const onSubmit = async (formData) => {
    setSaving(true)
    const sanitizedData = formDataSanitizer(formData, module, dropdownOptions)

    request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/lead/${latestLeadId}/lead_details`)
    try {
      const response = await request.modify(sanitizedData)
      if (response.status === 200 || response.status === 201) {
        setSaving(false)
        handleCancel()
        dispatch(setNotification({ type: 'success', body: '' }))
        const pathSegments = window.location.pathname.split('/');
        const moduleName = pathSegments[1];
        navi(`/${moduleName}/${customerId}`, { state: { reloadPage: true } });
      } else {
        setSaving(false)
        dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
      }
    } catch (error) {
      setSaving(false)
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
    }
  };

  const formRender = () => {
    if (moduleFields && module) {
      const filterAndSortByCustomFields = (moduleFields, leadFields) => {
        return moduleFields
          .filter((e) => leadFields.includes(e.column_name))
          .sort((a, b) => {
            return leadFields.indexOf(a.column_name) - leadFields.indexOf(b.column_name);
          });
      };

      const filteredFields1 = filterAndSortByCustomFields(moduleFields, leadFields);

      const renderFields = (fields) => {
        // Until this opening's lead has loaded, render nothing rather than the previous one's fields.
        if (!leadInfo || loadedLeadId !== latestLeadId) {
          return null;
        }
        return fields.map((m, index) => {
          const fieldType = dropdownOptions[m.module_field_type_dropdown_option_id]?.code_key;
          const selectOptions = {
            dropdownOptions: dropDowns?.data.find((e) => m.dropdown_key === e.code_key)?.dropdown_options,
            is_editable: true,
            fieldType,
          };
          const fieldNameLookup = { zip_code_id: leadInfo.zip_code_label }

          const defaultValue = fieldNameLookup[m.column_name] || leadInfo[m.column_name];

          return (
            <FormFieldBuilder
              field={m}
              selectOptions={selectOptions}
              register={register}
              errors={errors}
              control={control}
              key={m.id}
              row={moduleFields}
              excludedFieldsFromOverride={[m.column_name]}
              sourceModule={{
                id: leadInfo[m.column_name],
                name: defaultValue,
                column_name: m.column_name,
              }}
              handleStateFields={(name, value) => { handleStateFields(name, value, module) }}
              stateFields={stateFields}
              data={defaultValue}
            />
          );
        });
      };

      return (
        <div className="fields-container">
          {renderFields(filteredFields1)}
        </div>
      );
    } else {
      return <Loading />;
    }
  };

  return active ? (
    <form onSubmit={handleSubmit(onSubmit)} id="lead_details_modal_form" className="lox-form">
      <div className="modal is-active">
        <div className="modal-background"></div>
        <div className="modal-card modal-card-lead-details column is-5">
          <header className="modal-card-head py-3">
            <p className="modal-card-title is-capitalized">
              Lead Details
            </p>
            <button className="delete" aria-label="close" onClick={handleCancel}></button>
          </header>
          <section
            className="modal-card-body modal-card-body"
            style={{ minHeight: "110px" }}
          >
            {formRender()}
          </section>
          <footer className="modal-card-foot is-justify-content-end py-1">
            <div className="lox-form__buttons-wrapper has-text-right">
              <button type="button" className="button is-small has-background-grey has-text-white" onClick={handleCancel}>Cancel</button>
              <button type="submit" className="button is-link is-small" disabled={saving && 'disabled'}>{saving ? 'Saving' : 'Save'}</button>
            </div>
          </footer>
        </div>
      </div>
    </form>
  ) : null;
}
